/**
 * The drawn page as places to put the caret, rather than as things that were drawn.
 *
 * {@link hitAt} answers "what did the pointer land on", which is the right question for a
 * note head and the wrong one for a bar: a bar is mostly empty space, and a click in that
 * space means "here, in this voice, at this moment" rather than nothing at all. An editor
 * that answers nothing there is not usable -- the caret can only be put on glyphs a few
 * pixels wide -- so the page is also read the other way round: every voice of every bar,
 * with the moments in it laid out left to right, and a point resolved to the nearest one.
 *
 * The geometry comes from two measurements that are already made for other reasons. Each
 * event's own box gives the moments; the bar's box gives the horizontal extent of the bar,
 * and it comes from the engine as a single element even in a score of many parts, because
 * an engine draws one bar across the whole system. Vertical bands are therefore taken from
 * the events of each voice, which is the only per-part geometry there is.
 */

import type { EventId, MeasureId, PartId } from "../document/ids";
import { eventOnsets, partMeasureAt } from "../document/query";
import type { Score } from "../document/types";
import { unionOf } from "./geometry";
import type { Box, Point } from "./geometry";
import { eventBox } from "./hit";
import type { Hit } from "./hit";
import type { Rendering } from "./render";

/** One moment of one voice, as it was drawn. */
export interface Slot {
  readonly eventId: EventId;
  /** Ticks from the start of the bar. */
  readonly onset: number;
  /** Everything drawn for the event, a chord's note heads taken together. */
  readonly box: Box;
  /** The first element the engine drew for it, for a caller that wants to name one. */
  readonly elementId: string;
}

/** One voice of one bar of one part, and the moments drawn in it. */
export interface DrawnVoice {
  readonly partId: PartId;
  readonly measureId: MeasureId;
  readonly measureIndex: number;
  readonly voiceIndex: number;
  /** The band this voice occupies: its own events, widened to the bar's width. */
  readonly box: Box;
  /** In time order, leaving out anything the engine did not draw on this page. */
  readonly slots: readonly Slot[];
}

/** One bar of the page, across every part that has something drawn in it. */
export interface DrawnBar {
  readonly measureId: MeasureId;
  readonly measureIndex: number;
  readonly box: Box;
  readonly voices: readonly DrawnVoice[];
}

/** A moment a click resolved to: everything a caret needs, and the event standing there. */
export interface Placement {
  readonly partId: PartId;
  readonly measureId: MeasureId;
  readonly measureIndex: number;
  readonly voiceIndex: number;
  /** Ticks from the start of the bar. */
  readonly onset: number;
  readonly slot: Slot;
}

/**
 * Read one page of a rendering as bars, voices and moments.
 *
 * `events` is what {@link useBoxes} measured; `bars` is the same for the bar elements, which
 * are measured separately because mixing them into one map would make every click inside a
 * bar land on the bar rather than on the note under the pointer.
 *
 * A voice with nothing drawn on this page is left out rather than given an empty band: the
 * band is derived from what was drawn, so there would be nothing to derive it from.
 */
export function drawnScore(
  score: Score,
  rendering: Rendering,
  events: ReadonlyMap<string, Box>,
  bars: ReadonlyMap<string, Box>,
  page = 1,
): readonly DrawnBar[] {
  const drawn: DrawnBar[] = [];

  for (let index = 0; index < score.measures.length; index += 1) {
    const measure = score.measures[index];
    if (measure === undefined) continue;

    const voices: DrawnVoice[] = [];
    for (const part of score.parts) {
      const bar = partMeasureAt(part, measure.id);
      if (bar === null) continue;
      bar.voices.forEach((voice, voiceIndex) => {
        const onsets = eventOnsets(voice);
        const slots: Slot[] = [];
        voice.events.forEach((event, at) => {
          if (rendering.eventPage.get(event.id) !== page) return;
          const box = eventBox(rendering, events, event.id);
          const elementId = rendering.eventElements.get(event.id)?.[0];
          if (box === null || elementId === undefined) return;
          slots.push({ eventId: event.id, onset: onsets[at] ?? 0, box, elementId });
        });
        const band = unionOf(slots.map((slot) => slot.box));
        if (band === null) return;
        voices.push({
          partId: part.id,
          measureId: measure.id,
          measureIndex: index,
          voiceIndex,
          box: band,
          slots,
        });
      });
    }

    if (voices.length === 0) continue;
    const span = bars.get(measure.id) ?? unionOf(voices.map((voice) => voice.box));
    if (span === null) continue;
    drawn.push({
      measureId: measure.id,
      measureIndex: index,
      box: span,
      voices: voices.map((voice) => ({ ...voice, box: widened(voice.box, span) })),
    });
  }

  return drawn;
}

/** A voice's band across the whole bar, so that a click anywhere in the bar finds it. */
function widened(band: Box, bar: Box): Box {
  return { x: bar.x, y: band.y, width: bar.width, height: band.height };
}

/** How far a point is from a box along one axis; zero when it is between its edges. */
function gap(from: number, to: number, at: number): number {
  return Math.max(from - at, 0, at - to);
}

function horizontal(box: Box, point: Point): number {
  return gap(box.x, box.x + box.width, point.x);
}

function vertical(box: Box, point: Point): number {
  return gap(box.y, box.y + box.height, point.y);
}

/**
 * The moment a point on the page means.
 *
 * The bar is chosen first, then the voice inside it, then the moment inside that -- rather
 * than by one distance over every slot -- because the three are measured differently. Bars
 * are picked by the point's distance to the whole band, voices by vertical distance alone
 * (they span the bar, so horizontal distance says nothing about which of them was meant),
 * and moments by horizontal distance alone (within a voice, that is what time is drawn as).
 *
 * Nothing is out of reach: a point below the last staff belongs to the nearest voice of the
 * nearest bar. A caller that wants a miss to mean nothing should check what it was given
 * against its own bounds, which this cannot do -- it has no idea where the page ends.
 */
export function placeAt(bars: readonly DrawnBar[], point: Point): Placement | null {
  const bar = nearest(bars, (candidate) => reach(candidate.box, point));
  if (bar === null) return null;

  const voice = nearest(bar.voices, (candidate) => vertical(candidate.box, point));
  if (voice === null) return null;

  const slot = nearest(voice.slots, (candidate) => horizontal(candidate.box, point));
  if (slot === null) return null;

  return {
    partId: voice.partId,
    measureId: voice.measureId,
    measureIndex: voice.measureIndex,
    voiceIndex: voice.voiceIndex,
    onset: slot.onset,
    slot,
  };
}

/** Distance from a point to a box, counting a point inside it as touching. */
function reach(box: Box, point: Point): number {
  return Math.hypot(horizontal(box, point), vertical(box, point));
}

/** The first of the candidates that scores lowest, or `null` when there are none. */
function nearest<T>(candidates: readonly T[], score: (candidate: T) => number): T | null {
  let best: T | null = null;
  let bestScore = Infinity;
  for (const candidate of candidates) {
    const value = score(candidate);
    if (value >= bestScore) continue;
    best = candidate;
    bestScore = value;
  }
  return best;
}

/**
 * What a click at a point selects, when it did not land on a note head.
 *
 * The answer is shaped like a hit on the event standing at that moment, because that is
 * what the editor does with a click anywhere: it selects an event and puts the caret on it.
 * The chord tone is always the first, since the point was not on a note head to begin with.
 */
export function slotHit(bars: readonly DrawnBar[], point: Point): Hit | null {
  const place = placeAt(bars, point);
  if (place === null) return null;
  return { event: place.slot.eventId, pitchIndex: 0, elementId: place.slot.elementId };
}

/**
 * Enough of a caret to find it on the page.
 *
 * The editor's own caret satisfies this without being imported: the rendering layer has no
 * business depending on the editing layer, and the four fields are the whole of what a
 * position is anyway.
 */
export interface CaretPosition {
  readonly partId: PartId;
  readonly measureId: MeasureId;
  readonly voiceIndex: number;
  /** Ticks from the start of the bar. */
  readonly onset: number;
}

/**
 * Where to draw the caret, or `null` when it stands on a bar this page did not draw.
 *
 * The caret is a line before the event it stands on, and after the last one when it stands
 * at the end of the bar -- which is where it lands after writing the note that fills it.
 * Its height is the taller of the voice's own band and an even share of the bar, because a
 * band drawn from a single whole rest is a few pixels high and a caret that size cannot be
 * seen; taking the share from the bar keeps the size proportional to the engraving rather
 * than to a number chosen here.
 */
export function caretBox(bars: readonly DrawnBar[], caret: CaretPosition, width = 2): Box | null {
  const bar = bars.find((candidate) => candidate.measureId === caret.measureId);
  if (bar === undefined) return null;
  const voice = bar.voices.find(
    (candidate) => candidate.partId === caret.partId && candidate.voiceIndex === caret.voiceIndex,
  );
  if (voice === undefined) return null;

  const at = voice.slots.find((slot) => slot.onset === caret.onset) ?? null;
  const before = at === null ? lastBefore(voice.slots, caret.onset) : null;
  const anchor = at ?? before;
  if (anchor === null) return null;

  const x = at === null ? anchor.box.x + anchor.box.width + 2 : anchor.box.x - width - 2;
  const height = Math.max(voice.box.height, bar.box.height / bar.voices.length);
  const middle = voice.box.y + voice.box.height / 2;
  return { x, y: middle - height / 2, width, height };
}

/** The last moment of a voice that starts before an onset, or `null` when none does. */
function lastBefore(slots: readonly Slot[], onset: number): Slot | null {
  let found: Slot | null = null;
  for (const slot of slots) {
    if (slot.onset < onset) found = slot;
  }
  return found;
}

/** Every bar element of a rendering drawn on a page, for measuring them in one pass. */
export function barsOnPage(rendering: Rendering, page: number): readonly string[] {
  const ids: string[] = [];
  for (const [measure, at] of rendering.measurePage) {
    if (at === page) ids.push(measure);
  }
  return ids;
}
